import React, { useContext, useState, useEffect } from 'react';

import { Box, Card, Container } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';

import { DialogContainer } from 'common/components';
import { LoadingContext } from 'common/utils/contexts';

import * as actions from './actions';
import { UserForm } from './components';
import ListItems from './components/ListItems';
import UsersPreviewTableToolbar from './components/UsersPreviewTableToolbar';
import * as selectors from './selectors';

const UsersContainer = ({
    downloadCSV,
    fetchRoles,
    fetchUsers,
    handleClickCloseModalUser,
    handleClickUpdate,
    isFetching,
    isFetchingCSV,
    isLoadingUserDetail,
    isOpenModalUser,
    match,
    pagination,
    reset,
    roles,
    rolesDictionary,
    users,
}) => {
    const { t } = useTranslation();
    const loadingContext = useContext(LoadingContext);

    const [filterData, setFilterData] = useState({
        orderBy: 'created_at',
        page: 0,
        pageSize: 25,
        searchText: '',
        sortBy: 'desc',
        status: 'active',
    });

    useEffect(() => {
        if (match.params.id) fetchRoles(match.params.id);

        return () => {
            reset();
        };
    }, []);

    useEffect(() => {
        if (match.params.id) {
            fetchUsers(match.params.id, filterData);
        }
    }, [filterData, match.params.id]);

    const handleOnSearchChange = (e) =>
        setFilterData((current) => ({ ...current, searchText: e }));

    const handleOnDownloadCSV = () => {
        downloadCSV(
            match.params.id,
            filterData.orderBy,
            filterData.sortBy,
            filterData.searchText,
            (text) => loadingContext.openLoading(text),
            () => {
                loadingContext.closeLoading();
            },
        );
    };

    return (
        <>
            <Container maxWidth="">
                <Box mb={2}>
                    <UsersPreviewTableToolbar
                        isFetching={isFetching}
                        isFetchingCSV={isFetchingCSV}
                        handleDownloadFile={handleOnDownloadCSV}
                        handleOnSearchChange={handleOnSearchChange}
                    />
                </Box>

                <Card>
                    <ListItems
                        companyId={match.params.id}
                        filterData={filterData}
                        filterItems={fetchUsers}
                        isFetching={isFetching}
                        items={users}
                        paginationData={pagination}
                        setFilterData={setFilterData}
                        handleClickUpdate={handleClickUpdate}
                    />
                </Card>
            </Container>
            <DialogContainer
                loading={isLoadingUserDetail}
                onClose={handleClickCloseModalUser}
                open={isOpenModalUser}
                fullWidth={true}
                title={t('Edit user')}
            >
                <UserForm roles={roles} rolesDictionary={rolesDictionary} />
            </DialogContainer>
        </>
    );
};

UsersContainer.propTypes = {
    downloadCSV: PropTypes.func,
    fetchRoles: PropTypes.func,
    fetchUsers: PropTypes.func,
    handleClickCloseModalUser: PropTypes.func,
    isFetching: PropTypes.bool,
    isFetchingCSV: PropTypes.bool,
    isLoadingUserDetail: PropTypes.bool,
    isOpenModalUser: PropTypes.bool,
    match: PropTypes.object,
    pagination: PropTypes.object,
    reset: PropTypes.func,
    roles: PropTypes.array,
    rolesDictionary: PropTypes.object,
    users: PropTypes.array,
    handleClickUpdate: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
    isFetching: selectors.getUsersIsFetching,
    isFetchingCSV: selectors.getIsFetchingUsersCSV,
    isOpenModalUser: selectors.getIsOpenModalUser,
    isLoadingUserDetail: selectors.getIsFetchingUserDetail,
    pagination: selectors.getUsersPagination,
    roles: selectors.getDataFetchRoles,
    rolesDictionary: selectors.getDictionaryFetchRoles,
    users: selectors.getUsersData,
});

const mapDispatchToProps = (dispatch) => ({
    downloadCSV: (id, orderBy, sortBy, searchStr, openLoading, closeLoading) =>
        dispatch(
            actions.downloadCSV(
                id,
                orderBy,
                sortBy,
                searchStr,
                openLoading,
                closeLoading,
            ),
        ),
    fetchRoles: (id) => dispatch(actions.fetchRoles(id)),
    fetchUsers: (id, filterData) =>
        dispatch(actions.fetchUsers(id, filterData)),
    handleClickCloseModalUser: () => dispatch(actions.toggleModalUser(false)),
    handleClickUpdate: (companyId, item) =>
        dispatch(actions.prepareUpdate(companyId, item)),
    reset: () => dispatch(actions.reset()),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withRouter(UsersContainer));
